import React, { useEffect, useState } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { endPoints } from "../../../constant/Environment";
import { addData, getAllData } from "../../../Utility/API";
import { ErrorToast, SuccesToast } from "../../../components/Toast/message";

const EMPTY = {
  chip_one: "",
  chip_two: "",
  chip_three: "",
  min_deposit: "",
  bonus_multiplier: "",
};

const ManualDepositConfigModal = ({ show, handleClose }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    const fetchConfig = async () => {
      setLoading(true);
      try {
        const res = await getAllData(endPoints.api.MANUAL_DEPOSIT_CONFIG);
        if (res?.status === "success" && res?.data) {
          setForm({
            chip_one: res.data.chip_one_usd ?? res.data.chip_one ?? "",
            chip_two: res.data.chip_two_usd ?? res.data.chip_two ?? "",
            chip_three: res.data.chip_three_usd ?? res.data.chip_three ?? "",
            min_deposit: res.data.min_deposit_usd ?? res.data.min_deposit ?? "",
            bonus_multiplier: res.data.bonus_multiplier ?? "",
          });
        } else {
          setForm(EMPTY);
        }
      } catch (e) {
        setForm(EMPTY);
        ErrorToast("Could not load manual deposit config");
      }
      setLoading(false);
    };
    fetchConfig();
  }, [show]);

  const onChange = (key) => (e) => {
    const raw = e.target.value;
    if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) {
      setForm((prev) => ({ ...prev, [key]: raw }));
    }
  };

  const chipsValid = ["chip_one", "chip_two", "chip_three"].every(
    (k) => Number(form[k]) > 0
  );
  const multiplierValid =
    form.bonus_multiplier !== "" && Number(form.bonus_multiplier) >= 0;
  const minValid = Number(form.min_deposit) > 0;
  const chipsAboveMin = ["chip_one", "chip_two", "chip_three"].every(
    (k) => Number(form[k]) >= Number(form.min_deposit)
  );

  const onSubmit = async () => {
    if (!chipsValid) {
      ErrorToast("All three chip amounts must be greater than 0");
      return;
    }
    if (!minValid) {
      ErrorToast("Minimum deposit must be greater than 0");
      return;
    }
    if (!chipsAboveMin) {
      ErrorToast("Chip amounts cannot be below the minimum deposit");
      return;
    }
    if (!multiplierValid) {
      ErrorToast("Bonus multiplier must be 0 or greater");
      return;
    }
    setSaving(true);
    try {
      const res = await addData(endPoints.api.MANUAL_DEPOSIT_CONFIG, {
        chip_one: Number(form.chip_one),
        chip_two: Number(form.chip_two),
        chip_three: Number(form.chip_three),
        min_deposit: Number(form.min_deposit),
        bonus_multiplier: Number(form.bonus_multiplier),
      });
      setSaving(false);
      if (res?.data?.status === "success") {
        SuccesToast(res?.data?.message || "Manual deposit config saved");
        handleClose();
      } else {
        ErrorToast(res?.data?.message || "Could not save config");
      }
    } catch (e) {
      setSaving(false);
      ErrorToast("Could not save config");
    }
  };

  const preview = Number(form.chip_two) * Number(form.bonus_multiplier);

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "18px" }}>
          Manual Deposit Config
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <p className="text-muted mb-0">Loading...</p>
        ) : (
          <>
            <p className="text-muted" style={{ fontSize: "13px" }}>
              All amounts are entered in USD and converted to each player's
              currency for display and validation.
            </p>

            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Chip 1 ($)</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="decimal"
                    placeholder="10"
                    value={form.chip_one}
                    onChange={onChange("chip_one")}
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Chip 2 ($)</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="decimal"
                    placeholder="20"
                    value={form.chip_two}
                    onChange={onChange("chip_two")}
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Chip 3 ($)</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="decimal"
                    placeholder="30"
                    value={form.chip_three}
                    onChange={onChange("chip_three")}
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Minimum Deposit ($)</Form.Label>
              <Form.Control
                type="text"
                inputMode="decimal"
                placeholder="10"
                value={form.min_deposit}
                onChange={onChange("min_deposit")}
              />
              <Form.Text className="text-muted">
                Set in USD. Players see and are held to this converted into
                their own currency using the same rate and multifactor as
                package prices.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Bonus Multiplier</Form.Label>
              <Form.Control
                type="text"
                inputMode="decimal"
                placeholder="1.5"
                value={form.bonus_multiplier}
                onChange={onChange("bonus_multiplier")}
              />
              <Form.Text className="text-muted">
                1 = same as deposit, 2 = double. Deposit stays as cash, this
                multiplier decides the bonus on top.
              </Form.Text>
            </Form.Group>

            {Number(form.chip_two) > 0 && form.bonus_multiplier !== "" ? (
              <div
                style={{
                  background: "#eaf7ee",
                  border: "1px solid #04631e33",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  fontSize: "13px",
                }}
              >
                Example: a <b>${Number(form.chip_two)}</b> deposit credits{" "}
                <b>${Number(form.chip_two).toFixed(2)}</b> cash +{" "}
                <b>${preview.toFixed(2)}</b> bonus.
              </div>
            ) : null}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          style={{ background: "#04631e", borderColor: "#04631e" }}
          onClick={onSubmit}
          disabled={loading || saving}
        >
          {saving ? "Saving..." : "Submit"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ManualDepositConfigModal;
